import { Link, createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import * as z from "zod/v4";
import type { HTTPError } from "ky";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { saveTokens } from "@/lib/http";
import { resendCompanyCode, verifyCompanyCode } from "@/lib/http/registration";
import { userInfoQueryOptions } from "@/lib/queries/auth";
import { parseRefusal } from "@/lib/utils/backend";

const searchSchema = z.object({
  email: z.string().optional(),
  expires: z.coerce.number().optional(),
  resend: z.coerce.number().optional(),
});
export const Route = createFileRoute("/auth/verify-code")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  staticData: { title: "Verify your email" },
});

function secondsText(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

function RouteComponent() {
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { email, expires = 3600, resend = 60 } = Route.useSearch();
  const [code, setCode] = useState("");
  const [expiresAt, setExpiresAt] = useState(() => Date.now() + expires * 1000);
  const [resendAt, setResendAt] = useState(() => Date.now() + resend * 1000);
  const [now, setNow] = useState(Date.now());
  const [message, setMessage] = useState<string | null>(null);
  const [attempts, setAttempts] = useState<number | null>(null);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const remaining = Math.max(0, Math.ceil((expiresAt - now) / 1000));
  const resendRemaining = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const verify = useMutation({
    mutationFn: (enteredCode: string) =>
      verifyCompanyCode({ email: email ?? "", code: enteredCode }),
    async onSuccess(data) {
      saveTokens({ access: data.access_token, refresh: data.refresh_token });
      await queryClient.invalidateQueries({
        queryKey: userInfoQueryOptions.queryKey,
      });
      navigate({ to: "/pricing", replace: true });
    },
    onError(error: HTTPError) {
      const detail =
        error.data && typeof error.data === "object" && "detail" in error.data
          ? error.data.detail
          : null;
      const refusal = parseRefusal(detail);
      setCode("");
      if (refusal?.code === "CODE_INVALID") {
        setAttempts(
          typeof refusal.attempts_remaining === "number"
            ? refusal.attempts_remaining
            : null,
        );
        setMessage(
          typeof refusal.attempts_remaining === "number"
            ? `That code is not correct. ${refusal.attempts_remaining} attempts left.`
            : "No code is awaiting verification for this address. Request a new one.",
        );
        return;
      }
      if (refusal?.code === "CODE_EXPIRED") {
        setMessage("This code has expired. Request a new one.");
        return;
      }
      if (refusal?.code === "TOO_MANY_ATTEMPTS") {
        setMessage("This code can no longer be used. Request a new one.");
        return;
      }
      if (refusal?.code === "CODE_CONSUMED") {
        setMessage("This account is already verified. Please log in.");
        return;
      }
      setMessage(
        typeof detail === "string"
          ? detail
          : (refusal?.message ??
              "We could not verify that code. Please try again."),
      );
    },
  });
  const resendCode = useMutation({
    mutationFn: () => resendCompanyCode(email ?? ""),
    onSuccess(data) {
      const delay = data.resend_available_in_seconds;
      setResendAt(Date.now() + delay * 1000);
      setExpiresAt(Date.now() + 3600 * 1000);
      setCode("");
      setAttempts(null);
      setMessage(data.message);
    },
    onError(error: HTTPError) {
      const detail =
        error.data && typeof error.data === "object" && "detail" in error.data
          ? error.data.detail
          : null;
      const refusal = parseRefusal(detail);
      const retry = refusal?.retry_after_seconds;
      if (typeof retry === "number") setResendAt(Date.now() + retry * 1000);
      setMessage(
        typeof detail === "string"
          ? detail
          : (refusal?.message ??
              "We could not send another code. Please try again."),
      );
    },
  });
  if (!email)
    return (
      <VerifyShell>
        <h1 className="text-2xl font-semibold">Missing email address</h1>
        <p className="text-muted-foreground">
          Start registration again to receive a verification code.
        </p>
        <Link to="/auth/signup" className="text-[#c11574] hover:underline">
          Create a workspace
        </Link>
      </VerifyShell>
    );
  const submit = () => {
    if (code.length === 6 && !verify.isPending) verify.mutate(code);
  };
  return (
    <VerifyShell>
      <h1 className="text-2xl font-semibold">Enter your verification code</h1>
      <p className="text-center text-muted-foreground">
        We sent a six-digit code to <strong>{email}</strong>.
      </p>
      <Input
        autoFocus
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(event) => {
          const next = event.target.value.replace(/\D/g, "").slice(0, 6);
          setCode(next);
          setMessage(null);
          if (next.length === 6)
            setTimeout(() => {
              if (!verify.isPending) verify.mutate(next);
            }, 0);
        }}
        className="h-14 text-center text-2xl tracking-[0.5em]"
        aria-label="Six digit verification code"
      />
      <Button onClick={submit} disabled={code.length !== 6 || verify.isPending}>
        Verify email
      </Button>
      <p className="text-sm text-muted-foreground">
        Code expires in {secondsText(remaining)}.
      </p>
      {attempts !== null && (
        <p className="text-sm text-destructive">{attempts} attempts left.</p>
      )}
      {message && (
        <p className="text-center text-sm text-destructive">{message}</p>
      )}
      <Button
        variant="outline"
        onClick={() => resendCode.mutate()}
        disabled={resendRemaining > 0 || resendCode.isPending}
      >
        {resendRemaining > 0
          ? `Resend code in ${secondsText(resendRemaining)}`
          : resendCode.isPending
            ? "Sending code..."
            : "Resend code"}
      </Button>
      <p className="text-sm">
        Already verified?{" "}
        <Link to="/auth/login" className="text-[#c11574] hover:underline">
          Log in
        </Link>
      </p>
    </VerifyShell>
  );
}

function VerifyShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-6 font-[Manrope,system-ui,sans-serif] dark:bg-background">
      <main className="flex w-full max-w-md flex-col items-center gap-5 text-center">
        {children}
      </main>
    </div>
  );
}
